/**
 * Watch Zone Alerts Panel
 * Shows proactive alerts when incidents occur in user's watch zones
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Bell,
  BellRing,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Shield,
  Radio,
  Users,
  Eye,
  Trash2,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_COLORS = {
  critical: 'bg-red-500 text-white',
  urgent: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  routine: 'bg-blue-500 text-white'
};

// Source icons
const SOURCE_ICONS = {
  scanner: Radio,
  community: Users,
  timeline: AlertTriangle,
  weather: Shield
};

const AlertCard = ({ alert, onMarkRead }) => {
  const priorityClass = PRIORITY_COLORS[alert.incident_priority] || PRIORITY_COLORS.routine;
  const SourceIcon = SOURCE_ICONS[alert.incident_source] || AlertTriangle;
  
  return (
    <div 
      className={`p-3 rounded-lg border ${
        alert.read 
          ? 'border-slate-700 bg-slate-800/30 opacity-60' 
          : 'border-teal-500/50 bg-teal-500/10'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${priorityClass}`}>
          <SourceIcon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-[10px] border-slate-600">
              {alert.zone_name}
            </Badge>
            <span className="text-[10px] text-slate-500">
              {alert.distance_miles} mi away
            </span>
          </div>
          
          <h4 className="text-sm font-medium text-slate-200 line-clamp-1">
            {alert.incident_title}
          </h4>
          
          {alert.incident_description && (
            <p className="text-xs text-slate-400 line-clamp-2 mt-1">
              {alert.incident_description}
            </p>
          )}
          
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {alert.time_ago}
            </span>
            
            {!alert.read && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMarkRead(alert.incident_id)}
                className="h-6 px-2 text-[10px] text-teal-400 hover:text-teal-300"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Mark Read
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AlertStats = ({ stats }) => {
  if (!stats) return null;
  
  return (
    <div className="grid grid-cols-3 gap-2 p-3 bg-slate-800/50 rounded-lg mb-4">
      <div className="text-center">
        <p className="text-lg font-bold text-white">{stats.total_alerts || 0}</p>
        <p className="text-[10px] text-slate-400">Total</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-red-400">{stats.critical_urgent || 0}</p>
        <p className="text-[10px] text-slate-400">Critical</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-teal-400">
          {Object.keys(stats.by_zone || {}).length}
        </p>
        <p className="text-[10px] text-slate-400">Zones</p>
      </div>
    </div>
  );
};

export const WatchZoneAlertsPanel = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const { showWatchZoneAlert, permission, requestPermission } = useNotifications();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/watch-zones/alerts?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/watch-zones/stats?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const checkNow = useCallback(async () => {
    if (!token) return;
    
    setChecking(true);
    try {
      const response = await fetch(`${API_URL}/api/watch-zones/check-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.alerts_found > 0) {
          // Refresh alerts
          await fetchAlerts();
          await fetchStats();
        }
      }
    } catch (error) {
      console.error('Failed to check zones:', error);
    } finally {
      setChecking(false);
    }
  }, [token, fetchAlerts, fetchStats]);

  const markAlertRead = useCallback(async (alertId) => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/watch-zones/alerts/${alertId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAlerts(prev => prev.map(a => 
        a.incident_id === alertId ? { ...a, read: true } : a
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark alert read:', error);
    }
  }, [token]);

  const markAllRead = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/watch-zones/alerts/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
      fetchStats();
    }
  }, [isOpen, fetchAlerts, fetchStats]);

  // Poll for new alerts every 30 seconds when panel is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [isOpen, fetchAlerts]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] p-0 glass-card border-slate-800">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <BellRing className="w-5 h-5 text-teal-400" />
                Watch Zone Alerts
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5">
                    {unreadCount}
                  </Badge>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={checkNow}
                  disabled={checking}
                  className="text-slate-400 hover:text-white h-8 px-2"
                >
                  <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Push Notification Permission */}
            {permission !== 'granted' && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-amber-300">Enable push notifications</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={requestPermission}
                    className="bg-amber-600 hover:bg-amber-500 h-7 text-xs"
                  >
                    Enable
                  </Button>
                </div>
              </div>
            )}

            {/* Stats */}
            <AlertStats stats={stats} />

            {/* Actions */}
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllRead}
                className="w-full text-xs"
              >
                <CheckCircle className="w-3 h-3 mr-2" />
                Mark All as Read
              </Button>
            )}
          </div>

          {/* Alerts List */}
          <ScrollArea className="flex-1 p-4">
            {loading && alerts.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-teal-400 animate-spin" />
              </div>
            ) : alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert, i) => (
                  <AlertCard 
                    key={alert.incident_id || i}
                    alert={alert}
                    onMarkRead={markAlertRead}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No alerts yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  You'll be notified when incidents occur in your watch zones
                </p>
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="p-3 border-t border-slate-700 text-center">
            <p className="text-[10px] text-slate-500">
              Alerts check every 60 seconds • Last 24 hours shown
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Bell button with badge for header
export const WatchZoneAlertButton = ({ onClick }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    
    const fetchUnread = async () => {
      try {
        const response = await fetch(`${API_URL}/api/watch-zones/alerts?unread_only=true&limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unread_count || 0);
        }
      } catch (error) {
        // Silently fail
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="text-slate-400 hover:text-teal-400 hover:bg-slate-800 relative"
      title="Watch Zone Alerts"
    >
      {unreadCount > 0 ? (
        <BellRing className="w-4 h-4 text-teal-400" />
      ) : (
        <Bell className="w-4 h-4" />
      )}
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );
};

export default WatchZoneAlertsPanel;
